import { Component, OnInit } from '@angular/core';
import { AlertService, ComponentsStoreService, FileStoreService, IAlertType, ProjectsStoreService, WarehouseStoreService } from '../../../_shared/services';
import { Components, Projects, WarehouseStockSerial } from '../../../_shared/interfaces';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { getTenantStorage } from '../../../_shared/utils/tenant-context';
import { HttpErrorResponse } from "@angular/common/http";
import { CurrencyPipe, DatePipe, NgForOf, NgIf } from "@angular/common";
import { ClrDatepickerModule, ClrIconModule, ClrInputModule, ClrNumberInputModule, ClrTextareaModule } from "@clr/angular";
import { ClarityIcons, twoWayArrowsIcon } from "@cds/core/icon";
import {ComponentFilesComponent} from "../component-files/component-files.component";
import {ComponentPartsComponent} from "../component-parts/component-parts.component";
import {UsedInProjectsComponent} from "./used-in-projects/used-in-projects.component";
import {UsedInJobsComponent} from "./used-in-jobs/used-in-jobs.component";
import {UsedInOrdersComponent} from "./used-in-orders/used-in-orders.component";
import { environment as env } from "../../../../environments/environment";
@Component({
  selector: 'app-component-details',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    CurrencyPipe,
    DatePipe,
    RouterLink,
    ClrIconModule,
    ClrInputModule,
    ClrNumberInputModule,
    ClrDatepickerModule,
    ClrTextareaModule,
    ReactiveFormsModule,
    ComponentFilesComponent,
    ComponentPartsComponent,
    UsedInProjectsComponent,
    UsedInJobsComponent,
    UsedInOrdersComponent
  ],
  templateUrl: './component-details.component.html',
  styleUrl: './component-details.component.sass'
})

export class ComponentDetailsComponent implements OnInit {
  components: Components[] = [];
  component: Components | null = null;
  projects: Projects[] = [];
  devMode: boolean;
  loading: boolean = true;
  countProjects: number = 0;
  technicalDrawing: string = '';
  stockSerials: WarehouseStockSerial[] = [];

  inventurOffen = false;
  inventurLaeuft = false;
  inventurDatei: File | null = null;
  /** Erst beim Oeffnen der Inventur aufgebaut -- vorher bewusst undefined. */
  inventurForm?: FormGroup;

  constructor(
    private componentsService: ComponentsStoreService,
    private pss: ProjectsStoreService,
    private waress: WarehouseStoreService,
    private alertDispatcher: AlertService,
    private fileService: FileStoreService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(twoWayArrowsIcon)
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.technicalDrawing = params['technical_drawing'];
      this.getComponent(this.technicalDrawing);
      this.getCountUsedInProjects(this.technicalDrawing);
      this.getStockSerials(this.technicalDrawing);
    });
  }

  getStockSerials(coid: string) {
    this.waress.getStockByDrawing(coid).subscribe({
      next: data => { this.stockSerials = data?.object ?? []; },
      error: () => { this.stockSerials = []; }
    });
  }

  movementLabel(type: string): string {
    switch (type) {
      case 'stock_init':        return 'Lager initialisiert';
      case 'incoming':          return 'Wareneingang';
      case 'outgoing':          return 'Warenausgang';
      case 'internal_transfer': return 'Interne Umbuchung';
      case 'return_inbound':    return 'Rückgabe Eingang';
      case 'return_outbound':   return 'Rückgabe Ausgang';
      case 'scrap':             return 'Ausschuss';
      case 'provision_in':      return 'Beistellung Eingang';
      case 'provision_out':     return 'Beistellung Ausgang';
      case 'inventory_in':      return 'Inventur (Zugang)';
      case 'inventory_out':     return 'Inventur (Abgang)';
      default:                  return type;
    }
  }

  // ── Inventur ─────────────────────────────────────────────────────────────

  /**
   * Oeffnet die Inventur. Vorbelegt wird der aktuelle Bestand, damit der
   * haeufigste Fall -- gezaehlt, stimmt -- ein Klick ist. Der Mitarbeiter
   * kommt aus der Sitzung; wer zaehlt, soll nicht auswaehlbar sein.
   */
  oeffneInventur(): void {
    // Der Clarity-Datepicker arbeitet in en-US und erwartet MM/TT/JJJJ.
    // Ein Date-Objekt als Startwert zeigt er nicht an.
    const h = new Date();
    const heute = `${String(h.getMonth() + 1).padStart(2, '0')}/${String(h.getDate()).padStart(2, '0')}/${h.getFullYear()}`;
    this.inventurDatei = null;
    this.inventurForm = this.fb.group({
      counted_pieces: [
        this.component?.number_of_pieces_in_stock ?? 0,
        [Validators.required, Validators.min(0)]
      ],
      date: [heute, Validators.required],
      comment: ['']
    });
    this.inventurOffen = true;
  }

  onInventurDatei(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.inventurDatei = input.files?.[0] ?? null;
  }

  /** Der Clarity-Datepicker liefert MM/TT/JJJJ (en-US). */
  private normalizeDate(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      const d = value as Date;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    }
    return String(value);
  }

  /** Erst den Beleg hochladen, dann buchen -- sonst haette die Buchung
   *  keine Datei-Id, an die sie sich haengen kann. */
  speichereInventur(): void {
    if (!this.inventurForm || this.inventurForm.invalid || this.inventurLaeuft) {
      return;
    }
    this.inventurLaeuft = true;

    if (!this.inventurDatei) {
      this.bucheInventur('');
      return;
    }

    this.fileService.sendFile(
      this.inventurDatei, 77, 0, '', 0, this.technicalDrawing,
      `Inventurbeleg ${this.technicalDrawing}`
    ).subscribe({
      next: (res: any) => this.bucheInventur(res?.data?.id ?? ''),
      error: () => {
        this.inventurLaeuft = false;
        this.alertDispatcher.show(
          { message: 'Datei-Upload fehlgeschlagen. Die Inventur wurde nicht gebucht.', clrAlertType: IAlertType.DANGER },
          5000
        );
      }
    });
  }

  private bucheInventur(fileId: string): void {
    const staffId = parseInt(getTenantStorage('staff_id') ?? '0', 10);
    if (!(staffId > 0)) {
      this.inventurLaeuft = false;
      this.alertDispatcher.show(
        { message: 'Kein Mitarbeiter in der Sitzung hinterlegt. Bitte neu anmelden.', clrAlertType: IAlertType.DANGER },
        5000
      );
      return;
    }

    this.waress.addInventory({
      technical_drawing: this.technicalDrawing,
      counted_pieces: Number(this.inventurForm!.value.counted_pieces),
      date: this.normalizeDate(this.inventurForm!.value.date),
      staff_id: staffId,
      file_id: fileId || undefined,
      comment: this.inventurForm!.value.comment || undefined
    }).subscribe({
      next: ergebnis => {
        this.inventurLaeuft = false;
        this.inventurOffen = false;
        this.inventurDatei = null;
        // Beide Ansichten neu laden: der Bestand steht im Bauteil, die
        // Bewegung in der Seriennummern-Tabelle.
        this.getComponent(this.technicalDrawing);
        this.getStockSerials(this.technicalDrawing);
        const d = ergebnis?.difference ?? 0;
        const text = d === 0
          ? `Inventur gebucht: ${ergebnis?.counted} Stück, keine Abweichung.`
          : `Inventur gebucht: ${ergebnis?.counted} Stück, Abweichung ${d > 0 ? '+' : ''}${d} (vorher ${ergebnis?.stock_before}).`;
        this.alertDispatcher.show(
          { message: text, clrAlertType: d === 0 ? IAlertType.SUCCESS : IAlertType.WARNING },
          6000
        );
      },
      error: (err: any) => {
        this.inventurLaeuft = false;
        this.alertDispatcher.show(
          { message: err?.error?.message ?? 'Inventur konnte nicht gebucht werden.', clrAlertType: IAlertType.DANGER },
          5000
        );
      }
    });
  }

  vorgangLabel(s: WarehouseStockSerial): string | null {
    if (s.last_delivery_id  && s.last_project_id) return `Lieferschein ${s.last_project_id}.0004.${s.last_delivery_id}`;
    if (s.last_transport_id && s.last_project_id) return `Transport ${s.last_project_id}.0005.${s.last_transport_id}`;
    if (s.last_order_id     && s.last_project_id) return `Order ${s.last_project_id}.0002.${s.last_order_id}`;
    if (s.last_job_id       && s.last_project_id) return `Job ${s.last_project_id}.0001.${s.last_job_id}`;
    return null;
  }

  vorgangLink(s: WarehouseStockSerial): any[] | null {
    if (s.last_delivery_id  && s.last_project_id && s.last_job_id)  return ['/delivery/details',  s.last_project_id, s.last_job_id,  s.last_delivery_id];
    if (s.last_transport_id && s.last_project_id && s.last_job_id)  return ['/transport/details', s.last_project_id, s.last_job_id,  s.last_transport_id, s.last_order_id ?? 0];
    if (s.last_order_id     && s.last_project_id)                   return ['/order/details',     s.last_project_id, s.last_order_id];
    if (s.last_job_id       && s.last_project_id)                   return ['/job/details',       s.last_project_id, s.last_job_id];
    return null;
  }

  getComponent(coid: string) {
    this.componentsService.getSingleComponent(coid).subscribe({
      next: data => {
          if (!data?.object?.length) {
            this.alertDispatcher.show({
              message: ' Keine Komponente gefunden! ',
              clrAlertType: IAlertType.WARNING
            }, 2000);
          }
          this.components = data.object;
          this.component = this.components[0] || null;
        },
        complete: () => {
          if (this.component && !this.component.active) {
            this.alertDispatcher.show({
              message: ' \'Die Komponente \' + this.components.name + \' (Zeichnungsnummer: \' + this.components.technical_drawing + \') wurde auf PASSIV gesetzt! ',
              clrAlertType: IAlertType.WARNING
            }, 2000);
          }
          this.loading = false;
        },
        error: () => {
          return
        }
    })
  }

  getCountUsedInProjects(coid: string) {
    this.pss.getUsedProjectsPPS(coid).subscribe({
      next: data => {
        if (!data || !data.object || data.object.length === 0) {
          this.alertDispatcher.show({
            message: ' Keine Projekte gefunden! ',
            clrAlertType: IAlertType.WARNING
          }, 2000);
        }
        this.projects = data.object;
      },
      complete: () => {
        this.countProjects = this.projects.length;
      },
      error: () => {
        return
      }
    });
  }

  editComponent(component: Components) {
    this.router.navigate(['/component/update', component.technical_drawing], { state: component });
  }

  private deleteFehlgeschlagen = false;

  deleteComponent(drawing_number: string, parent: number) {
    if (confirm('Soll die Komponente ' + drawing_number + ' wirklich gelöscht werden?\nDiese Aktion kann nicht rückgängig gemacht werden!')) {
      this.componentsService.deleteComponent(drawing_number, parent)
        .subscribe({
          next: data => {
            if (!data) {
              // Ohne dieses Flag lief der Erfolgshinweis auch im Fehlerfall
              // durch, und complete() navigierte trotzdem zur Übersicht.
              this.deleteFehlgeschlagen = true;
              this.alertDispatcher.show({
                message: ' Löschen der Komponente fehlgeschlagen! ',
                clrAlertType: IAlertType.WARNING
              }, 5000);
              return;
            }
            this.alertDispatcher.show({
              message: ' Komponente ' + drawing_number + ' wurde erfolgreich gelöscht! ',
              clrAlertType: IAlertType.INFO
            }, 5000);
          },
          complete: () => {
            if (!this.deleteFehlgeschlagen) {
              this.router.navigate(['/component/overview/true']);
            }
            this.deleteFehlgeschlagen = false;
          },
          error: (err: any) => {
            this.deleteFehlgeschlagen = true;
            // Die Begründung des Servers anzeigen statt sie zu verschlucken:
            // RMS nennt bei 409, wo das Bauteil noch verwendet wird.
            const grund = (err instanceof HttpErrorResponse ? err.error?.message : null)
              || 'Beim Löschen ist ein Fehler aufgetreten.';
            this.alertDispatcher.show({
              message: ' ' + grund + ' ',
              clrAlertType: IAlertType.DANGER
            }, 10000);
          }
        });
    }
  }
}
